import { useState, useEffect } from 'react';
import { supabase } from '../../../integrations/supabase/client';

export interface PreviewMessage {
  id: string;
  nodeId: string;
  type: 'text' | 'image' | 'audio' | 'video' | 'document';
  content: string | null;
  mediaUrl: string | null;
  caption: string | null;
  fileName: string | null;
  sequenceOrder: number;
  aiDescription: string | null;
  aiProcessingStatus: 'pending' | 'processing' | 'completed' | 'error' | null;
  duration: number | null;
  groupId: string | null;
  instruction: string | null;
}

export interface GroupedMessages {
  groupId: string;
  instruction: string | null;
  messages: PreviewMessage[];
  groupNumber: number;
}

export const useWhatsAppPreview = (flowId: string | null) => {
  const [messages, setMessages] = useState<PreviewMessage[]>([]);
  const [groupedMessages, setGroupedMessages] = useState<GroupedMessages[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!flowId) {
      setMessages([]);
      setGroupedMessages([]);
      setIsLoading(false);
      return;
    }

    const fetchMessages = async () => {
      setIsLoading(true);
      try {
        // Obtener todos los mensajes del flujo
        const { data: messagesData, error: messagesError } = await supabase
          .from('training_messages')
          .select('*')
          .eq('flow_id', flowId)
          .order('sequence_order', { ascending: true });

        if (messagesError) throw messagesError;

        // Obtener grupos del flujo
        const { data: groupsData, error: groupsError } = await supabase
          .from('training_groups')
          .select('*')
          .eq('flow_id', flowId);

        if (groupsError) throw groupsError;

        // Construir set de node_ids válidos desde node_order de cada grupo
        // Esto es la fuente de verdad: solo mostrar mensajes que existen en los grupos
        const validNodeIds = new Set<string>();
        (groupsData || []).forEach(group => {
          const nodeOrder = group.node_order as any[] | null;
          if (Array.isArray(nodeOrder)) {
            nodeOrder.forEach((entry: any) => {
              if (entry?.node_id) validNodeIds.add(entry.node_id);
            });
          }
        });

        // Filtrar solo mensajes válidos (que existen en node_order de algún grupo)
        // y deduplicar por node_id (mantener el más reciente)
        const validMessages = (messagesData || []).filter(
          msg => validNodeIds.has(msg.node_id)
        );
        const deduplicatedMessages = validMessages.reduce((acc: any[], msg: any) => {
          const existing = acc.find(m => m.node_id === msg.node_id);
          if (existing) {
            const existingDate = existing.updated_at || existing.created_at || '';
            const newDate = msg.updated_at || msg.created_at || '';
            if (newDate > existingDate) {
              return acc.map(m => m.node_id === msg.node_id ? msg : m);
            }
            return acc;
          }
          return [...acc, msg];
        }, []);

        // Transformar mensajes al formato de preview
        const transformedMessages: PreviewMessage[] = deduplicatedMessages.map(msg => {
          // Para imágenes, videos y documentos: text_content es el caption del usuario (puede ser null)
          // Para texto: text_content es el contenido del mensaje
          // Para audio: ai_description es la transcripción
          const isMediaMessage = ['image', 'video', 'document'].includes(msg.node_type);
          
          return {
            id: msg.id,
            nodeId: msg.node_id,
            type: msg.node_type as 'text' | 'image' | 'audio' | 'video' | 'document',
            content: msg.text_content,
            mediaUrl: msg.media_url,
            // Para mensajes multimedia, solo mostrar caption si existe y no está vacío
            caption: isMediaMessage ? (msg.text_content?.trim() || null) : msg.text_content,
            fileName: msg.file_name,
            sequenceOrder: msg.sequence_order || 0,
            aiDescription: msg.ai_description,
            aiProcessingStatus: msg.ai_processing_status as 'pending' | 'processing' | 'completed' | 'error' | null,
            duration: msg.duration,
            groupId: msg.group_id,
            instruction: null,
          };
        });

        // Agrupar mensajes por group_id y ordenar grupos numéricamente
        const grouped = groupsData
          ?.map((group, index) => {
            const groupMessages = transformedMessages.filter(msg => msg.groupId === group.group_id);
            const metadata = group.metadata as Record<string, any> | null;
            return {
              groupId: group.group_id,
              instruction: group.instruction,
              messages: groupMessages.sort((a, b) => a.sequenceOrder - b.sequenceOrder),
              groupNumber: metadata?.group_number || index + 1
            };
          })
          .sort((a, b) => a.groupNumber - b.groupNumber) || [];

        setMessages(transformedMessages);
        setGroupedMessages(grouped);
      } catch (error) {
        console.error('Error fetching preview messages:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();

    // Suscribirse a cambios en tiempo real para training_messages
    const messagesChannel = supabase
      .channel('training-messages-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'training_messages',
          filter: `flow_id=eq.${flowId}`
        },
        () => {
          console.log('Mensaje actualizado, recargando preview...');
          fetchMessages();
        }
      )
      .subscribe();

    // Suscribirse a cambios en training_groups
    const groupsChannel = supabase
      .channel('training-groups-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'training_groups',
          filter: `flow_id=eq.${flowId}`
        },
        () => {
          console.log('Grupo actualizado, recargando preview...');
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(groupsChannel);
    };
  }, [flowId]);

  return { messages, groupedMessages, isLoading };
};
